"use client";

// Visual availability/booking calendar with two views:
//   Month  — full-size month grid
//   Year   — 12 mini-months, same markers, each day still clickable
//
// Day markers (color = status, texture = booking type):
//   emerald          available (inside an availability window, nothing booked)
//   rose solid       booked — PRIVATE (exclusive) booking
//   rose striped     booked — SHARED (prorata) booking
//   amber dashed     pending request
//   grey             past day / outside any availability window

import { useMemo, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api-client";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/inline-dialog";
import { ChevronLeft, ChevronRight, Home, Users2, Lock, Plus, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatPrice } from "@/components/booking/format";
import { useI18n } from "@/lib/i18n/provider";

type StayMember = {
  id: string;
  userId: string | null;
  name: string;
  share: number;
  isOrganiser: boolean;
};

type CalendarProperty = {
  id: string;
  title: string;
  propertyType: string;
  canValidate?: boolean;
  canEdit?: boolean;
  availabilities: { startDate: string; endDate: string }[];
  bookings: {
    id: string;
    userId: string;
    startDate: string;
    endDate: string;
    status: "APPROVED" | "PENDING";
    partySize: number;
    bookingType: string;
    guestName: string;
    participants?: StayMember[];
  }[];
};

const DAY_MS = 86400000;

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function isoDay(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}
/** DST-safe: add n whole calendar days to a date. */
function addCalendarDays(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
/** Availability windows / reservations end on the checkout day (inclusive):
 *  the stored end is the last millisecond of the last occupied day, so the
 *  end date itself IS marked. Compared as calendar-date keys: grid cells are
 *  local midnights while stored dates are UTC midnights, so raw instants can
 *  mismatch by the TZ offset. */
function coversDayKey(startIso: string, endIso: string, dayKey: string) {
  return dayKey >= startIso.slice(0, 10) && dayKey <= endIso.slice(0, 10);
}

type DayCell = {
  day: Date;
  inMonth: boolean;
  isPast: boolean;
  marker:
    | "available"
    | "bookedPrivate"
    | "bookedShared"
    | "pending"
    | "passed"
    | "off";
  tooltips: string[];
  // Guests registered for that day, in the order they should be stacked
  // (index 0 = bottom line of the cell).
  names: { name: string; status: "APPROVED" | "PENDING" }[];
};

// How many guest names fit in a full-size month cell (plus an ellipsis line).
const MAX_CELL_NAMES = 3;

function buildMonthGrid(
  property: CalendarProperty,
  month: Date,
  today: Date
): DayCell[] {
  const first = startOfMonth(month);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  // DST-safe: build each cell from calendar-day arithmetic (fixed 24h steps
  // duplicate a date across the October clock change).
  return Array.from({ length: 42 }, (_, i) => {
    const day = addCalendarDays(first, i - startOffset);
    const dayKey = isoDay(day);
    const inMonth = day.getMonth() === month.getMonth();
    const isPast = day.getTime() < today.getTime();
    const available = property.availabilities.some((a) =>
      coversDayKey(a.startDate, a.endDate, dayKey)
    );
    const approved = property.bookings.filter(
      (b) => b.status === "APPROVED" && coversDayKey(b.startDate, b.endDate, dayKey)
    );
    const pending = property.bookings.filter(
      (b) => b.status === "PENDING" && coversDayKey(b.startDate, b.endDate, dayKey)
    );
    let marker: DayCell["marker"] = "off";
    if (approved.some((b) => b.bookingType === "PRIVATE")) marker = "bookedPrivate";
    else if (approved.some((b) => b.bookingType === "SHARED")) marker = "bookedShared";
    else if (pending.length > 0) marker = "pending";
    else if (available) marker = "available";
    // Past days are never clickable — free ones get the dark "passed" tone
    // instead of the inviting green.
    if (isPast && marker === "available") marker = "passed";
    const tooltips = [
      ...approved.map(
        (b) => `${b.guestName} · ${b.partySize}p · ${b.bookingType} · ${b.status}`
      ),
      ...pending.map((b) => `${b.guestName || "?"} · ${b.partySize}p · PENDING`),
    ];
    const names = [
      ...approved.map((b) => ({ name: b.guestName || "?", status: "APPROVED" as const })),
      ...pending.map((b) => ({ name: b.guestName || "?", status: "PENDING" as const })),
    ];
    return { day, inMonth, isPast, marker, tooltips, names };
  });
}

const CELL_CLS: Record<DayCell["marker"], string> = {
  available:
    "bg-zinc-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-800",
  passed:
    "bg-zinc-300 text-zinc-600 border-zinc-400 dark:bg-zinc-800 dark:text-zinc-500 dark:border-zinc-700",
  bookedPrivate:
    "bg-zinc-100 text-rose-900 border-rose-400 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-900",
  bookedShared:
    "bg-sky-50 text-sky-900 border-sky-500 dark:bg-sky-950 dark:text-sky-200 dark:border-sky-700",
  pending:
    "bg-zinc-100 text-amber-900 border-amber-400 border-dashed dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800",
  off: "bg-zinc-100 text-zinc-400 border-zinc-200 dark:bg-zinc-900 dark:text-zinc-500 dark:border-zinc-800",
};

export function AvailabilityCalendar() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [propertyId, setPropertyId] = useState<string>("");
  const [view, setView] = useState<"month" | "year">("month");
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const permissions = useAppStore((s) => s.permissions);
  const user = useAppStore((s) => s.user);
  const canBook = permissions.includes("bookings.manage");  const [bookingDay, setBookingDay] = useState<Date | null>(null);
  const [bookForm, setBookForm] = useState({
    bookingType: "PRIVATE" as "PRIVATE" | "SHARED",
    start: "",
    end: "",
    partySize: 1,
    userId: "",
    // Members sharing a private stay (the organiser is added server-side).
    participantIds: [] as string[],
  });
  // The organiser of a new booking: "myself" resolves to the signed-in user.
  const effectiveUserId = bookForm.userId || (user?.id ?? "");
  const [busy, setBusy] = useState(false);


  const { data, isLoading } = useQuery({
    queryKey: ["calendar"],
    queryFn: () => apiGet<{ properties: CalendarProperty[] }>("/api/admin/calendar"),
  });

  const properties = data?.properties ?? [];
  const selectedId = propertyId || properties[0]?.id || "";
  const property = properties.find((p) => p.id === selectedId);
  // Approve/reject rights come from the server per property (admin, owning
  // owner, or house staff assigned to that specific house).
  const canValidate = property?.canValidate ?? false;
  const { data: dashData } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () =>
      apiGet<{ users: { id: string; name: string; username: string; role: string }[] }>(
        "/api/admin/dashboard"
      ),
    enabled: !!bookingDay,
  });
  const bookableUsers = dashData?.users ?? [];

  const createBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!property || !bookForm.start || !bookForm.end) return;
    setBusy(true);
    try {
      await apiPost("/api/admin/bookings", {
        productId: property.id,
        bookingType: bookForm.bookingType,
        userId: bookForm.userId || undefined,
        startDate: bookForm.start,
        endDate: bookForm.end,
        partySize: Math.max(1, bookForm.partySize),
        // Members sharing a private stay (the target user is always included).
        participantIds:
          bookForm.bookingType === "PRIVATE" ? bookForm.participantIds : undefined,
      });
      toast.success(t("calendar.bookingAdded"));
      qc.invalidateQueries({ queryKey: ["calendar"] });
      setBookingDay(null);
    } catch (err: any) {
      toast.error(err.message || "Could not add booking");
    } finally {
      setBusy(false);
    }
  };

  const openBookingFor = (day: Date, preferred: "PRIVATE" | "SHARED" = "PRIVATE") => {
    setBookForm({
      bookingType: preferred,
      start: isoDay(day),
      end: isoDay(addCalendarDays(day, 1)),
      partySize: 1,
      userId: "",
      participantIds: [],
    });
    setBookingDay(day);
  };

  // Clicking an occupied day shows what occupies it instead of the add form.
  const [dayDetails, setDayDetails] = useState<Date | null>(null);
  // Which reservation is currently being approved/rejected (disable buttons).
  const [decideId, setDecideId] = useState<string | null>(null);
  // Stay members: inline add/remove inside the day panel.
  const [addMemberFor, setAddMemberFor] = useState<string | null>(null);
  const [memberBusy, setMemberBusy] = useState(false);
  const canEditApproved = permissions.includes("bookings.editAfterApproval");
  const [cancelTarget, setCancelTarget] = useState<
    CalendarProperty["bookings"][number] | null
  >(null);
  const openDay = (day: Date) => {
    const dayKey = isoDay(day);
    const hasBookings = property?.bookings.some(
      (b) =>
        (b.status === "APPROVED" || b.status === "PENDING") &&
        coversDayKey(b.startDate, b.endDate, dayKey)
    );
    if (hasBookings) setDayDetails(day);
    else if (canBook) openBookingFor(day);
  };

  // Members of a private stay: add / remove straight from the day panel.
  const addStayMember = async (reservationId: string, userId: string) => {
    setMemberBusy(true);
    try {
      await apiPost(`/api/reservations/${reservationId}/participants`, { userId });
      toast.success(t("stay.memberAdded"));
      setAddMemberFor(null);
      qc.invalidateQueries({ queryKey: ["calendar"] });
      qc.invalidateQueries({ queryKey: ["reservations"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (err: any) {
      toast.error(err.message || "Could not add the member");
    } finally {
      setMemberBusy(false);
    }
  };

  const removeStayMember = async (reservationId: string, userId: string) => {
    setMemberBusy(true);
    try {
      await apiDelete(
        `/api/reservations/${reservationId}/participants?userId=${encodeURIComponent(userId)}`
      );
      toast.success(t("stay.memberRemoved"));
      qc.invalidateQueries({ queryKey: ["calendar"] });
      qc.invalidateQueries({ queryKey: ["reservations"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (err: any) {
      toast.error(err.message || "Could not remove the member");
    } finally {
      setMemberBusy(false);
    }
  };

  // Validate (approve/reject) a pending request straight from the day dialog.
  const decideBooking = async (id: string, action: "approve" | "reject") => {
    setDecideId(id);
    try {
      await apiPatch(`/api/reservations/${id}`, { action });
      toast.success(
        action === "approve"
          ? t("toast.reservationApproved")
          : t("toast.reservationRejected")
      );
      qc.invalidateQueries({ queryKey: ["calendar"] });
      // The host panel shows the same pending list — keep it fresh too.
      qc.invalidateQueries({ queryKey: ["reservations"] });
    } catch (err: any) {
      toast.error(err.message || "Could not save");
    } finally {
      setDecideId(null);
    }
  };

  const dayBookings = useMemo(() => {
    if (!property || !dayDetails) return [];
    const dayKey = isoDay(dayDetails);
    return property.bookings
      .filter(
        (b) =>
          (b.status === "APPROVED" || b.status === "PENDING") &&
          coversDayKey(b.startDate, b.endDate, dayKey)
      )
      .sort((a, b) => (a.status === b.status ? b.partySize - a.partySize : a.status === "APPROVED" ? -1 : 1));
  }, [property, dayDetails]);

  const dayIsExclusive = dayBookings.some(
    (b) => b.bookingType === "PRIVATE" && b.status === "APPROVED"
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Which stay types are possible for the date range being booked?
  const approvedCoveringRange =
    property && bookForm.start && bookForm.end
      ? property.bookings.filter(
          (b) =>
            b.status === "APPROVED" &&
            b.startDate.slice(0, 10) < bookForm.end &&
            b.endDate.slice(0, 10) > bookForm.start
        )
      : [];
  const rangeHasAny = approvedCoveringRange.length > 0;
  const rangeHasPrivate = approvedCoveringRange.some((b) => b.bookingType === "PRIVATE");
  const privateAllowed = !rangeHasAny;
  const sharedAllowed = !rangeHasPrivate;
  const typeValid =
    bookForm.bookingType === "PRIVATE" ? privateAllowed : sharedAllowed;

  // Keep the selected booking type valid when the range only allows one.
  useEffect(() => {
    if (!bookingDay) return;
    setBookForm((f) => {
      if (f.bookingType === "PRIVATE" && !privateAllowed && sharedAllowed)
        return { ...f, bookingType: "SHARED" };
      if (f.bookingType === "SHARED" && !sharedAllowed && privateAllowed)
        return { ...f, bookingType: "PRIVATE" };
      return f;
    });
  }, [bookingDay, bookForm.start, bookForm.end, privateAllowed, sharedAllowed]);

  // Users already booked (pending/approved) on the selected date range.
  const bookedUserIds =
    property && bookForm.start && bookForm.end
      ? property.bookings
          .filter(
            (b) =>
              (b.status === "APPROVED" || b.status === "PENDING") &&
              b.startDate.slice(0, 10) < bookForm.end &&
              b.endDate.slice(0, 10) > bookForm.start
          )
          .map((b) => b.userId)
      : [];

  const monthDays = useMemo(
    () => (property ? buildMonthGrid(property, month, today) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [property, month]
  );

  const dateFmt = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(
      locale === "fr" ? "fr-FR" : locale === "de" ? "de-DE" : "en-GB",
      opts
    );

  const monthLabel = dateFmt({ month: "long", year: "numeric" }).format(month);
  const dayHeaders = useMemo(() => {
    const fmt = dateFmt({ weekday: "short" });
    return Array.from({ length: 7 }, (_, i) =>
      fmt.format(new Date(2024, 0, 1 + i))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const dayCell = (cell: DayCell, size: "full" | "mini", key: string) => {
    const { day, inMonth, isPast, marker, tooltips } = cell;
    // Staff calendar: past days stay clickable (retro-active bookings) and
    // booked past days keep a colored outline as history.
    const clickable = canBook;
    const pastBooked =
      isPast && (marker === "bookedPrivate" || marker === "bookedShared");
    const base =
      size === "full"
        ? "relative flex h-16 flex-col rounded-lg border p-1 text-start text-xs shadow-sm dark:shadow-none"
        : "relative flex aspect-square items-center justify-center rounded-[4px] border text-[9px]";
    const cls = `${base} ${
      pastBooked
        ? "bg-zinc-300 text-zinc-700 border-2 dark:bg-zinc-800 dark:text-zinc-400"
        : CELL_CLS[marker]
    } ${inMonth ? "" : "opacity-60"} ${
      clickable ? "cursor-pointer transition-all hover:ring-2 hover:ring-primary/40 hover:shadow-md hover:-translate-y-px" : ""
    }`;
    const borderStyle =
      pastBooked
        ? {
            borderColor:
              marker === "bookedPrivate"
                ? "var(--color-rose-900)"
                : "var(--color-sky-700)",
          }
        : undefined;
    const title = tooltips.length > 0 ? tooltips.join("\n") : undefined;

    const content =
      size === "full" ? (
        <>
          <span
            className={`font-medium ${
              day.getTime() === today.getTime()
                ? "underline decoration-2 underline-offset-2"
                : ""
            }`}
          >
            {day.getDate()}
          </span>
          {cell.names.length > 0 ? (
            <span className="mt-auto flex flex-col justify-end gap-[1px] text-[9px] leading-[10px]">
              {cell.names.length > MAX_CELL_NAMES && (
                <span className="font-semibold">…</span>
              )}
              {cell.names
                .slice(0, MAX_CELL_NAMES)
                .reverse()
                .map((n, idx) => (
                  <span
                    key={idx}
                    className={`truncate ${
                      n.status === "PENDING"
                        ? "text-amber-800 dark:text-amber-300"
                        : ""
                    }`}
                  >
                    {n.name}
                  </span>
                ))}
            </span>
          ) : marker === "available" ? (
            <span className="mt-auto truncate text-[10px] leading-tight text-emerald-800">
              {t("calendar.availableShort")}
            </span>
          ) : marker === "passed" ? (
            <span className="mt-auto truncate text-[10px] leading-tight text-zinc-600 dark:text-zinc-500">
              {t("calendar.passedShort")}
            </span>
          ) : null}
        </>
      ) : (
        <span className="font-medium">{day.getDate()}</span>
      );

    return clickable ? (
      <button
        key={key}
        type="button"
        title={title}
        onClick={() => openDay(day)}
        style={borderStyle}
        className={cls}
      >
        {content}
      </button>
    ) : (
      <div key={key} title={title} style={borderStyle} className={cls}>
        {content}
      </div>
    );
  };

  return (
    <div>
      {/* Controls */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={selectedId}
            onValueChange={setPropertyId}
            disabled={properties.length === 0}
          >
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder={t("calendar.chooseProperty")} />
            </SelectTrigger>
            <SelectContent>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {property && (
            <Badge variant="outline" className="capitalize">
              {property.title.length > 24
                ? property.title.slice(0, 24) + "…"
                : property.title}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-foreground">
            <Button
              size="sm"
              variant="ghost"
              className={`h-8 rounded-none border-0 ${
                view === "month"
                  ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
                  : "bg-background text-foreground hover:bg-muted"
              }`}
              onClick={() => setView("month")}
            >
              {t("calendar.viewMonth")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className={`h-8 rounded-none border-0 ${
                view === "year"
                  ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
                  : "bg-background text-foreground hover:bg-muted"
              }`}
              onClick={() => setView("year")}
            >
              {t("calendar.viewYear")}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              variant="outline"
              onClick={() =>
                view === "month"
                  ? setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
                  : setMonth(new Date(month.getFullYear() - 1, month.getMonth(), 1))
              }
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[130px] text-center text-sm font-medium capitalize">
              {view === "month" ? monthLabel : month.getFullYear()}
            </span>
            <Button
              size="icon"
              variant="outline"
              onClick={() =>
                view === "month"
                  ? setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
                  : setMonth(new Date(month.getFullYear() + 1, month.getMonth(), 1))
              }
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-emerald-300 bg-zinc-100" />
          {t("calendar.legendAvailable")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-rose-400 bg-zinc-100" />
          {t("calendar.legendBookedPrivate")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-sky-500 bg-sky-50" />
          {t("calendar.legendBookedShared")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-dashed border-amber-400 bg-zinc-100" />
          {t("calendar.legendPending")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-zinc-300 bg-zinc-100" />
          {t("calendar.legendOff")}
        </span>
      </div>

      {isLoading ? (
        <Skeleton className="h-72 w-full rounded-xl" />
      ) : !property ? (
        <div className="grid place-items-center rounded-xl border border-dashed py-12 text-sm text-muted-foreground">
          {t("calendar.noProperties")}
        </div>
      ) : view === "month" ? (
        <div>
          <div className="mb-1 grid grid-cols-7 gap-1">
            {dayHeaders.map((d) => (
              <div
                key={d}
                className="text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthDays.map((cell, i) => dayCell(cell, "full", String(i)))}
          </div>
        </div>
      ) : (
        /* Year view: 12 mini-months, same markers, days clickable */
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 12 }, (_, m) => {
            const miniMonth = new Date(month.getFullYear(), m, 1);
            const cells = buildMonthGrid(property, miniMonth, today);
            const bookedDays = cells.filter(
              (c) =>
                c.inMonth &&
                (c.marker === "bookedPrivate" || c.marker === "bookedShared")
            ).length;
            const pendingDays = cells.filter(
              (c) => c.inMonth && c.marker === "pending"
            ).length;
            return (
              <div key={m} className="rounded-lg border bg-card p-2">
                <button
                  type="button"
                  className="mb-1 flex w-full items-center justify-between rounded px-1 text-xs font-semibold capitalize hover:bg-muted"
                  title={canBook ? t("calendar.bookMonth") : undefined}
                  onClick={() =>
                    canBook ? openBookingFor(miniMonth) : undefined
                  }
                  disabled={!canBook}
                >
                  <span>{dateFmt({ month: "long" }).format(miniMonth)}</span>
                  <span className="flex items-center gap-1 text-[10px] font-normal text-muted-foreground">
                    {bookedDays > 0 && (
                      <span className="text-rose-600 dark:text-rose-400">
                        {bookedDays}
                        {t("calendar.daySuffix")}
                      </span>
                    )}
                    {pendingDays > 0 && (
                      <span className="text-amber-600 dark:text-amber-400">
                        {pendingDays}
                        {t("calendar.daySuffixP")}
                      </span>
                    )}
                  </span>
                </button>
                <div className="grid grid-cols-7 gap-[3px]">
                  {cells.map((cell, i) => dayCell(cell, "mini", `${m}-${i}`))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canBook && (
        <p className="mt-2 text-xs text-muted-foreground">
          {view === "month" ? t("calendar.clickHint") : t("calendar.clickHintYear")}
        </p>
      )}

      {/* Day details: what occupies a clicked, already-occupied day */}
      <Dialog open={!!dayDetails} onOpenChange={(o) => !o && setDayDetails(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("calendar.dayDetails", {
                date: dayDetails
                  ? dateFmt({ day: "numeric", month: "long", year: "numeric" }).format(dayDetails)
                  : "",
              })}
            </DialogTitle>
            <DialogDescription>{property?.title}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            {dayBookings.map((b) => (
              <div key={b.id} className="rounded-lg border bg-card px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 font-medium">
                    {b.bookingType === "SHARED" ? (
                      <Users2 className="h-4 w-4 text-sky-600" />
                    ) : (
                      <Lock className="h-4 w-4 text-rose-500" />
                    )}
                    {b.guestName || "—"}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge
                      variant="outline"
                      className={
                        b.status === "APPROVED"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-amber-200 bg-amber-50 text-amber-800"
                      }
                    >
                      {b.status === "APPROVED"
                        ? t("calendar.status.APPROVED")
                        : t("calendar.status.PENDING")}
                    </Badge>
                    {b.status === "PENDING" && canValidate && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={decideId === b.id}
                          className="h-7 gap-1 border-emerald-200 bg-emerald-50 px-2 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300 dark:hover:bg-emerald-900/60"
                          onClick={() => decideBooking(b.id, "approve")}
                        >
                          <Check className="h-3.5 w-3.5" /> {t("host.approve")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={decideId === b.id}
                          className="h-7 gap-1 border-rose-200 bg-rose-50 px-2 text-rose-700 hover:bg-rose-100 hover:text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300 dark:hover:bg-rose-900/60"
                          onClick={() => decideBooking(b.id, "reject")}
                        >
                          <X className="h-3.5 w-3.5" /> {t("host.decline")}
                        </Button>
                      </>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      title={t("calendar.cancelBooking")}
                      onClick={() => setCancelTarget(b)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {b.bookingType === "SHARED"
                    ? t("booking.sharedTitle")
                    : t("booking.privateTitle")}{" "}
                  · {t("guests")}: {b.partySize} · {formatDate(b.startDate, locale)} —{" "}
                  {formatDate(b.endDate, locale)}
                </div>

                {/* Members sharing a private stay + their equal split */}
                {b.bookingType === "PRIVATE" && (
                  <div className="mt-2 border-t pt-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-medium text-muted-foreground">
                        {t("stay.members")}:
                      </span>
                      {(b.participants ?? []).map((m) => (
                        <span
                          key={m.id}
                          className="inline-flex items-center gap-1 rounded-full border bg-muted/60 px-2 py-0.5 text-xs"
                        >
                          <span className={m.isOrganiser ? "font-medium" : ""}>
                            {m.name}
                            {m.isOrganiser ? " · " + t("stay.organiser") : ""}
                          </span>
                          <span className="text-muted-foreground">
                            {formatPrice(m.share)}
                          </span>
                          {!m.isOrganiser && m.userId && (
                            <button
                              type="button"
                              disabled={
                                memberBusy ||
                                (b.status === "APPROVED" && !canEditApproved)
                              }
                              title={t("stay.removeMember")}
                              onClick={() => removeStayMember(b.id, m.userId as string)}
                              className="ml-0.5 text-muted-foreground transition-colors hover:text-destructive disabled:opacity-40"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      ))}
                      {(b.participants ?? []).length > 1 && (
                        <span className="text-xs text-muted-foreground">
                          {t("stay.each", {
                            amount: formatPrice((b.participants ?? [])[0]?.share ?? 0),
                          })}
                        </span>
                      )}
                    </div>

                    {/* Lock rule: approved stays are frozen unless permitted */}
                    {b.status === "APPROVED" && (
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        {canEditApproved ? t("stay.lockedPerm") : t("stay.locked")}
                      </p>
                    )}

                    {/* Inline member picker (no popup) */}
                    {b.status !== "APPROVED" || canEditApproved ? (
                      addMemberFor === b.id ? (
                        <div className="mt-2 rounded-lg border bg-muted/40 p-2">
                          <div className="mb-1.5 flex items-center justify-between">
                            <span className="text-xs font-medium">
                              {t("stay.addMember")}
                            </span>
                            <button
                              type="button"
                              className="text-xs text-muted-foreground hover:text-foreground"
                              onClick={() => setAddMemberFor(null)}
                            >
                              {t("common.cancel")}
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {bookableUsers
                              .filter(
                                (u) =>
                                  !(b.participants ?? []).some((m) => m.userId === u.id)
                              )
                              .map((u) => (
                                <Button
                                  key={u.id}
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={memberBusy}
                                  className="h-7 gap-1 px-2 text-xs"
                                  onClick={() => addStayMember(b.id, u.id)}
                                >
                                  <Plus className="h-3 w-3" /> {u.name || u.username}
                                </Button>
                              ))}
                            {bookableUsers.filter(
                              (u) => !(b.participants ?? []).some((m) => m.userId === u.id)
                            ).length === 0 && (
                              <span className="text-xs text-muted-foreground">
                                {t("mod.noUsers")}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="mt-1 h-7 gap-1 px-2 text-xs"
                          onClick={() => setAddMemberFor(b.id)}
                        >
                          <Plus className="h-3 w-3" /> {t("stay.addMember")}
                        </Button>
                      )
                    ) : null}
                  </div>
                )}
              </div>
            ))}
            {dayIsExclusive && (
              <p className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
                {t("calendar.exclusiveNote")}
              </p>
            )}
          </div>
          {!dayIsExclusive && canBook && dayDetails && (
            <DialogFooter>
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={() => {
                  const d = dayDetails;
                  setDayDetails(null);
                  openBookingFor(d);
                }}
              >
                <Plus className="h-4 w-4" /> {t("calendar.addHere")}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" /> {t("calendar.cancelTitle")}
            </DialogTitle>
            <DialogDescription>
              {cancelTarget
                ? `${cancelTarget.guestName || "—"} · ${formatDate(
                    cancelTarget.startDate,
                    locale
                  )} — ${formatDate(cancelTarget.endDate, locale)} · ${t(
                    `booking.${cancelTarget.bookingType}`
                  )}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
            {t("calendar.cancelAck")}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!cancelTarget) return;
                setBusy(true);
                try {
                  const res = await apiPatch(
                    `/api/reservations/${cancelTarget.id}`,
                    { action: "cancel" }
                  );
                  toast.success(t("calendar.cancelled"));
                  setCancelTarget(null);
                  setDayDetails(null);
                  qc.invalidateQueries({ queryKey: ["calendar"] });
                  void res;
                } catch (err: any) {
                  toast.error(err.message || "Could not cancel");
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t("calendar.cancelConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add booking from a clicked day / month */}
      <Dialog open={!!bookingDay} onOpenChange={(o) => !o && setBookingDay(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("calendar.addBooking")}</DialogTitle>
            <DialogDescription>
              {property?.title} ·{" "}
              {bookingDay
                ? t("calendar.bookDay", {
                    date: dateFmt({
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    }).format(bookingDay),
                  })
                : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createBooking} className="grid gap-4">
            {/* Booking type chooser — only the options that are actually possible */}
            <div className={`grid gap-2 ${privateAllowed && sharedAllowed ? "grid-cols-2" : "grid-cols-1"}`}>
              {privateAllowed && (
                <button
                  type="button"
                  onClick={() => setBookForm((f) => ({ ...f, bookingType: "PRIVATE" }))}
                  className={`rounded-lg border p-2.5 text-start text-sm transition ${
                    bookForm.bookingType === "PRIVATE"
                      ? "border-rose-400 bg-rose-50 text-rose-900 ring-1 ring-rose-300"
                      : "border-border bg-card text-muted-foreground hover:border-sky-200"
                  }`}
                >
                  <div className="font-medium">{t("booking.privateTitle")}</div>
                  <div className="mt-0.5 text-[11px] leading-tight">{t("booking.PRIVATE.short")}</div>
                </button>
              )}
              {sharedAllowed && (
                <button
                  type="button"
                  onClick={() => setBookForm((f) => ({ ...f, bookingType: "SHARED" }))}
                  className={`rounded-lg border p-2.5 text-start text-sm transition ${
                    bookForm.bookingType === "SHARED"
                      ? "border-sky-400 bg-sky-50 text-sky-900 ring-1 ring-sky-300"
                      : "border-border bg-card text-muted-foreground hover:border-sky-200"
                  }`}
                >
                  <div className="font-medium">{t("booking.sharedTitle")}</div>
                  <div className="mt-0.5 text-[11px] leading-tight">{t("booking.SHARED.short")}</div>
                </button>
              )}
            </div>
            {!privateAllowed && sharedAllowed && (
              <p className="text-xs text-muted-foreground">
                {t("booking.onlySharedHint")}
              </p>
            )}
            {privateAllowed && !sharedAllowed && (
              <p className="text-xs text-muted-foreground">
                {t("booking.onlyPrivateHint")}
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>{t("detail.checkIn")}</Label>
                <Input
                  type="date"
                  required
                  value={bookForm.start}
                  onChange={(e) =>
                    setBookForm((f) => ({
                      ...f,
                      start: e.target.value,
                      end: f.end && f.end <= e.target.value ? "" : f.end,
                    }))
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("detail.checkOut")}</Label>
                <Input
                  type="date"
                  required
                  min={bookForm.start}
                  value={bookForm.end}
                  onChange={(e) => setBookForm((f) => ({ ...f, end: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>{t("guests")}</Label>
                <Input
                  type="number"
                  min={1}
                  required
                  value={bookForm.partySize}
                  onChange={(e) =>
                    setBookForm((f) => ({ ...f, partySize: Number(e.target.value) || 1 }))
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("calendar.bookFor")}</Label>
                <Select
                  value={bookForm.userId || "myself"}
                  onValueChange={(v) =>
                    setBookForm((f) => ({ ...f, userId: v === "myself" ? "" : v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="myself">{t("calendar.myself")}</SelectItem>
                    {bookableUsers.map((u) => {
                      const already = bookedUserIds.includes(u.id);
                      return (
                        <SelectItem key={u.id} value={u.id} disabled={already}>
                          {u.name} (@{u.username})
                          {already ? ` — ${t("calendar.alreadyBooked")}` : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t("calendar.approvedHint")}</p>

            {/* Members sharing a private stay: the price is split between them */}
            {bookForm.bookingType === "PRIVATE" && (
              <div className="grid gap-2 rounded-lg border bg-muted/30 p-3">
                <div>
                  <div className="text-sm font-medium">{t("booking.stayMembers")}</div>
                  <p className="text-xs text-muted-foreground">
                    {t("booking.stayMembersHint")}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {bookableUsers
                    .filter((u) => u.id !== effectiveUserId)
                    .map((u) => {
                      const already = bookedUserIds.includes(u.id);
                      const picked = bookForm.participantIds.includes(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          disabled={already}
                          title={
                            already ? t("calendar.alreadyBooked") : u.name || u.username
                          }
                          onClick={() =>
                            setBookForm((f) => ({
                              ...f,
                              participantIds: picked
                                ? f.participantIds.filter((x) => x !== u.id)
                                : [...f.participantIds, u.id],
                            }))
                          }
                          className={`rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-40 ${
                            picked
                              ? "border-primary bg-primary text-primary-foreground"
                              : "bg-card text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          {u.name || u.username}
                        </button>
                      );
                    })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("stay.people", { count: bookForm.participantIds.length + 1 })}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBookingDay(null)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={busy || !typeValid} className="bg-emerald-600 hover:bg-emerald-700">
                {t("calendar.addBooking")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CalendarPropertyIcon() {
  return <Home className="h-4 w-4" />;
}
